/** Feature-aligned rock faces. Collision owns the polyhedra; this adapter only
 * batches their exact faces and supplies the existing terrain shader's fields.
 * These meshes have independent lifetime from heightfield LOD children. */
import * as THREE from 'three';
import type {CanyonCliffField,CanyonCliffSolid,PlanetSampler,TerrainSample} from '@hyperlight/shared';

const CELL_M=256;

export class CanyonCliffMeshes {
  readonly group=new THREE.Group();
  readonly triangles:number;
  readonly bytes:number;
  constructor(sampler:PlanetSampler,field:CanyonCliffField,material:THREE.Material) {
    this.group.name='canyon-cliff-volumes';
    const batches=new Map<string,CanyonCliffSolid[]>();
    for(const solid of field.solids){
      const key=solid.center.map(x=>Math.floor(x/CELL_M)).join(',');
      let batch=batches.get(key);if(!batch)batches.set(key,batch=[]);batch.push(solid);
    }
    let triangles=0,bytes=0;
    try { for(const solids of batches.values()){
      const center=new THREE.Vector3(...solids[0].center);
      const count=solids.reduce((n,s)=>n+s.positions.length/3,0);
      const indexCount=solids.reduce((n,s)=>n+s.indices.length,0);
      const positions=new Float32Array(count*3),normals=new Float32Array(count*3),ppos=new Float32Array(count*3);
      const fields=new Float32Array(count*4),slopes=new Float32Array(count),canyon=new Float32Array(count*2);
      const zero=new Float32Array(count),zero2=new Float32Array(count*2),zero3=new Float32Array(count*3);
      const road=new Float32Array(count*2),indices=new Uint32Array(indexCount);
      let vertex=0,index=0;const sample={}as TerrainSample;
      for(const solid of solids){
        const radius=Math.hypot(...solid.center);
        sampler.sample(solid.center[0]/radius,solid.center[1]/radius,solid.center[2]/radius,sample,true);
        for(let j=0;j<solid.positions.length;j+=3){
          const i=vertex+j/3,o=i*3;
          const x=solid.center[0]+solid.positions[j],y=solid.center[1]+solid.positions[j+1],z=solid.center[2]+solid.positions[j+2],r=Math.hypot(x,y,z);
          positions[o]=x-center.x;positions[o+1]=y-center.y;positions[o+2]=z-center.z;
          ppos[o]=x;ppos[o+1]=y;ppos[o+2]=z;
          normals[o]=solid.normals[j];normals[o+1]=solid.normals[j+1];normals[o+2]=solid.normals[j+2];
          slopes[i]=(normals[o]*x+normals[o+1]*y+normals[o+2]*z)/r;
          road[i*2]=8;road[i*2+1]=sample.snowRetention??-1;
          fields[i*4]=(r-sampler.def.radius)/sampler.amplitude;
          fields[i*4+2]=sample.temp;fields[i*4+3]=sample.moist;canyon[i*2]=1;
        }
        for(const i of solid.indices)indices[index++]=vertex+i;
        vertex+=solid.positions.length/3;
      }
      const geometry=new THREE.BufferGeometry();
      const attributes=new Map<Float32Array,THREE.BufferAttribute>();
      for(const[name,data,size]of [
        ['position',positions,3],['normal',normals,3],['ppos',ppos,3],['field',fields,4],['slope',slopes,1],
        ['aCanyonSurface',canyon,2],['riverFlow',zero3,3],['aRiverS',zero,1],['aRoadSd',road,2],
        ['aVolcanism',zero,1],['aDuneSand',zero,1],['aGlacierIce',zero2,2],
      ]as const){
        let attribute=attributes.get(data);if(!attribute){attribute=new THREE.BufferAttribute(data,size);attributes.set(data,attribute);}
        geometry.setAttribute(name,attribute);
      }
      geometry.setIndex(new THREE.BufferAttribute(indices,1));geometry.computeBoundingBox();geometry.computeBoundingSphere();
      const mesh=new THREE.Mesh(geometry,material);mesh.position.copy(center);
      mesh.name='canyon-cliff-batch';mesh.castShadow=true;mesh.receiveShadow=true;
      mesh.userData.cliffSolidIds=solids.map(s=>s.id);this.group.add(mesh);
      triangles+=indices.length/3;
      bytes+=[positions,normals,ppos,fields,slopes,canyon,zero,zero2,zero3,road,indices].reduce((n,a)=>n+a.byteLength,0);
    }} catch(error){this.dispose();throw error;}
    this.triangles=triangles;this.bytes=bytes;
  }
  dispose():void {
    for(const mesh of this.group.children as THREE.Mesh[])mesh.geometry.dispose();
    this.group.removeFromParent();this.group.clear();
  }
}
